import React, { useState, useEffect, useMemo } from 'react';
import { Download, Search, Database, CircleDollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { materialsAPI, inventoryAPI, reportsAPI, suppliersAPI, settingsAPI } from '../lib/api';
import { toast } from 'sonner';
import { useSuperUser } from '../context/SuperUserContext';
import { SuperUserDialog } from '../components/SuperUserDialog';

// Import extracted components
import {
  InventoryStats,
  MaterialsTable,
  MovementsTable,
  MovementsSheet,
  MaterialDialog,
  MovementDialog,
  EditMovementDialog,
  InitialStockCostDialog,
  SupplierDialog,
} from '../components/inventory';

// Unit conversion helper
const convertUnits = (quantity, fromUnit, toUnit, sackWeight = 100, sackWeightUnit = 'lb') => {
  if (fromUnit === toUnit) return quantity;
  let sackFactor = sackWeight;
  if (sackWeightUnit === 'kg') {
    sackFactor = sackWeight * 2.20462;
  }
  const conversions = {
    lb: 1.0,
    kg: 2.20462,
    qq: 100.0,
    ton: 2204.62,
    saco: sackFactor,
  };
  const fromFactor = conversions[fromUnit] || 1;
  const toFactor = conversions[toUnit] || 1;
  return (quantity * fromFactor) / toFactor;
};

export default function InventoryPage() {
  // Core data state
  const [materials, setMaterials] = useState([]);
  const [movements, setMovements] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [exchangeRate, setExchangeRate] = useState(60);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Super User
  const { isSuperUser } = useSuperUser();
  const [superUserDialogOpen, setSuperUserDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Material form state
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [newMaterial, setNewMaterial] = useState({
    name: '', unit: '', currency: '', current_stock: 0, min_stock: 0, average_cost: 0,
    sack_weight: 100, sack_weight_unit: 'lb'
  });
  
  // Movement form state
  const [movementForm, setMovementForm] = useState({
    material_id: '', type: 'entrada', quantity: 0, input_unit: 'qq',
    unit_cost: 0, currency: '', purchase_date: new Date().toISOString().split('T')[0],
    supplier_id: '', supplier: '', invoice_number: '', notes: '',
    sack_weight: 100, sack_weight_unit: 'lb'
  });
  
  // Edit movement state
  const [editMovementDialogOpen, setEditMovementDialogOpen] = useState(false);
  const [editingMovement, setEditingMovement] = useState(null);
  const [editMovementForm, setEditMovementForm] = useState({
    quantity: 0, input_unit: 'qq', unit_cost: 0, currency: 'DOP',
    purchase_date: '', supplier_id: '', supplier: '', invoice_number: '', notes: ''
  });
  
  // Initial stock/cost dialog (combined)
  const [initialStockCostDialogOpen, setInitialStockCostDialogOpen] = useState(false);
  const [initialStockCostForm, setInitialStockCostForm] = useState({ 
    material_id: '', quantity: 0, unit: 'qq', cost: 0, currency: 'DOP' 
  });
  
  // Supplier dialog
  const [newSupplierDialogOpen, setNewSupplierDialogOpen] = useState(false);
  const [newSupplierForm, setNewSupplierForm] = useState({ name: '', rnc: '', phone: '', email: '' });
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Filter state
  const [selectedMaterialFilter, setSelectedMaterialFilter] = useState('all');

  // Conversion preview calculation
  const conversionPreview = useMemo(() => {
    if (!movementForm.material_id || movementForm.quantity <= 0) return null;
    const material = materials.find(m => m.id === movementForm.material_id);
    if (!material) return null;
    
    const sackWeight = movementForm.sack_weight || 100;
    const sackWeightUnit = movementForm.sack_weight_unit || 'lb';
    
    const convertedQty = convertUnits(
      movementForm.quantity, movementForm.input_unit, material.unit, sackWeight, sackWeightUnit
    );
    
    let sackFactor = sackWeight;
    if (sackWeightUnit === 'kg') sackFactor = sackWeight * 2.20462;
    
    const conversions = { lb: 1.0, kg: 2.20462, qq: 100.0, ton: 2204.62, saco: sackFactor };
    const convertedCost = movementForm.unit_cost > 0 
      ? movementForm.unit_cost / (conversions[movementForm.input_unit] / conversions[material.unit])
      : 0;
    
    return { quantity: convertedQty, baseUnit: material.unit, costPerBaseUnit: convertedCost, materialName: material.name };
  }, [movementForm, materials]);

  // Data fetching
  const fetchData = async () => {
    setLoading(true);
    try {
      const [materialsRes, movementsRes, suppliersRes, rateRes] = await Promise.all([
        materialsAPI.getAll(),
        inventoryAPI.getMovements(),
        suppliersAPI.getAll(),
        settingsAPI.getExchangeRate(),
      ]);
      setMaterials(materialsRes.data);
      setMovements(movementsRes.data);
      setSuppliers(suppliersRes.data);
      setExchangeRate(rateRes.data.rate || 60);
    } catch (error) {
      toast.error('Error al cargar inventario');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Handlers
  const resetMaterialForm = () => {
    setEditingMaterial(null);
    setNewMaterial({ name: '', unit: '', currency: '', current_stock: 0, min_stock: 0, average_cost: 0, sack_weight: 100, sack_weight_unit: 'lb' });
    setMaterialDialogOpen(false);
  };

  const handleCreateMaterial = async (e) => {
    e.preventDefault();
    if (!newMaterial.unit) { toast.error('Selecciona la unidad de almacén'); return; }
    if (!newMaterial.currency) { toast.error('Selecciona la moneda de costos'); return; }
    
    try {
      const materialData = {
        name: newMaterial.name, unit: newMaterial.unit, currency: newMaterial.currency,
        min_stock: newMaterial.min_stock, current_stock: newMaterial.current_stock, average_cost: newMaterial.average_cost
      };
      if (newMaterial.unit === 'saco') {
        materialData.sack_weight = newMaterial.sack_weight;
        materialData.sack_weight_unit = newMaterial.sack_weight_unit;
      }
      
      if (editingMaterial) {
        await materialsAPI.update(editingMaterial.id, materialData);
        toast.success('Material actualizado');
      } else {
        await materialsAPI.create(materialData);
        toast.success('Material creado exitosamente');
      }
      resetMaterialForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar material');
    }
  };

  const handleEditMaterial = (material) => {
    if (!isSuperUser) {
      setPendingAction({ type: 'edit_material', data: material });
      setSuperUserDialogOpen(true);
      return;
    }
    executeEditMaterial(material);
  };

  const executeEditMaterial = (material) => {
    setEditingMaterial(material);
    setNewMaterial({
      name: material.name, unit: material.unit, currency: material.currency || 'DOP',
      current_stock: material.current_stock, min_stock: material.min_stock, average_cost: material.average_cost,
      sack_weight: material.sack_weight || 100, sack_weight_unit: material.sack_weight_unit || 'lb'
    });
    setMaterialDialogOpen(true);
  };

  const handleToggleDeduct = async (materialId, enabled) => {
    try {
      await materialsAPI.update(materialId, { deduct_enabled: enabled });
      toast.success(enabled ? 'Descuento activado' : 'Descuento desactivado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar configuración');
    }
  };

  const handleDeleteMaterial = (id) => {
    if (!isSuperUser) {
      setPendingAction({ type: 'delete_material', data: id });
      setSuperUserDialogOpen(true);
      return;
    }
    setDeleteType('material');
    setItemToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCreateMovement = async (e) => {
    e.preventDefault();
    if (!movementForm.supplier_id) { toast.error('Selecciona un proveedor'); return; }
    if (!movementForm.currency) { toast.error('Selecciona la moneda'); return; }
    
    try {
      const selectedSupplier = suppliers.find(s => s.id === movementForm.supplier_id);
      const movementData = {
        ...movementForm,
        input_unit: movementForm.input_unit,
        unit: movementForm.input_unit,
        supplier: selectedSupplier ? selectedSupplier.name : movementForm.supplier,
        sack_weight: movementForm.input_unit === 'saco' ? movementForm.sack_weight : null,
        sack_weight_unit: movementForm.input_unit === 'saco' ? movementForm.sack_weight_unit : null,
      };
      await inventoryAPI.createMovement(movementData);
      
      if (conversionPreview && movementForm.type === 'entrada') {
        toast.success(`Compra registrada: ${movementForm.quantity} ${movementForm.input_unit} → ${conversionPreview.quantity.toFixed(2)} ${conversionPreview.baseUnit}`);
      } else {
        toast.success(`${movementForm.type === 'entrada' ? 'Compra registrada' : 'Salida registrada'}`);
      }
      
      setMovementForm({ 
        material_id: '', type: 'entrada', quantity: 0, input_unit: 'qq', unit_cost: 0, currency: '',
        purchase_date: new Date().toISOString().split('T')[0], supplier_id: '', supplier: '',
        invoice_number: '', notes: '', sack_weight: 100, sack_weight_unit: 'lb'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar movimiento');
    }
  };

  const handleEditMovement = (movement) => {
    if (!isSuperUser) {
      setPendingAction({ type: 'edit_movement', data: movement });
      setSuperUserDialogOpen(true);
      return;
    }
    executeEditMovement(movement);
  };

  const executeEditMovement = (movement) => {
    setEditingMovement(movement);
    setEditMovementForm({
      quantity: movement.quantity, input_unit: movement.input_unit || movement.unit || 'qq',
      unit_cost: movement.unit_cost || 0, currency: movement.currency || 'DOP',
      purchase_date: movement.purchase_date || '', supplier_id: movement.supplier_id || '',
      supplier: movement.supplier || '', invoice_number: movement.invoice_number || '', notes: movement.notes || ''
    });
    setEditMovementDialogOpen(true);
  };

  const handleUpdateMovement = async (e) => {
    e.preventDefault();
    if (!editingMovement) return;
    try {
      await inventoryAPI.updateMovement(editingMovement.id, editMovementForm);
      toast.success('Movimiento actualizado');
      setEditMovementDialogOpen(false);
      setEditingMovement(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar movimiento');
    }
  };

  const handleDeleteMovement = (id) => {
    if (!isSuperUser) {
      setPendingAction({ type: 'delete_movement', data: id });
      setSuperUserDialogOpen(true);
      return;
    }
    setDeleteType('movement');
    setItemToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      if (deleteType === 'material') {
        await materialsAPI.delete(itemToDelete);
        toast.success('Material eliminado');
      } else if (deleteType === 'movement') {
        await inventoryAPI.deleteMovement(itemToDelete);
        toast.success('Movimiento eliminado y stock actualizado');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      setDeleteType(null);
    }
  };

  const handleOpenInitialStockCost = () => {
    if (!isSuperUser) { 
      setPendingAction({ type: 'initial_stock_cost', data: null }); 
      setSuperUserDialogOpen(true); 
      return; 
    }
    setInitialStockCostDialogOpen(true);
  };

  const handleSaveInitialStockCost = async () => {
    if (!initialStockCostForm.material_id) {
      toast.error('Selecciona un material');
      return;
    }
    const overrideMode = !!initialStockCostForm.override;
    if (!overrideMode && initialStockCostForm.quantity <= 0) { 
      toast.error('Ingresa una cantidad válida'); 
      return; 
    }
    try {
      const material = materials.find(m => m.id === initialStockCostForm.material_id);
      
      const currentStock = material.current_stock || 0;
      const currentCost = material.average_cost || 0;
      const newQuantity = initialStockCostForm.quantity || 0;
      const newCost = initialStockCostForm.cost || 0;

      let updateData;
      let toastMsg;

      if (overrideMode) {
        // Modo Sobrescribir: reemplaza directamente stock y costo (sin promedio ponderado)
        updateData = {
          ...material,
          current_stock: newQuantity,
          average_cost: newCost,
          currency: initialStockCostForm.currency,
        };
        toastMsg = `Sobrescrito: Stock=${newQuantity} ${material.unit}, Costo=${newCost.toFixed(2)} ${initialStockCostForm.currency}/${material.unit}`;
      } else {
        // Modo Sumar / Ponderar (comportamiento anterior)
        const newStock = currentStock + newQuantity;
        updateData = { ...material, current_stock: newStock };
        if (newCost > 0) {
          const totalValue = (currentStock * currentCost) + (newQuantity * newCost);
          const weightedAverageCost = newStock > 0 ? totalValue / newStock : newCost;
          updateData.average_cost = parseFloat(weightedAverageCost.toFixed(2));
          updateData.currency = initialStockCostForm.currency;
        }
        const costMsg = newCost > 0 ? ` | Costo Prom: ${updateData.average_cost?.toFixed(2)}` : '';
        toastMsg = `Stock actualizado: +${newQuantity} ${initialStockCostForm.unit} (Total: ${newStock.toFixed(2)} ${material.unit})${costMsg}`;
      }
      
      await materialsAPI.update(initialStockCostForm.material_id, updateData);
      toast.success(toastMsg);
      setInitialStockCostDialogOpen(false);
      setInitialStockCostForm({ material_id: '', quantity: 0, unit: 'qq', cost: 0, currency: 'DOP', override: false });
      fetchData();
    } catch (error) { 
      console.error('Error saving initial stock:', error);
      toast.error('Error al guardar stock inicial'); 
    }
  };

  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    if (!newSupplierForm.name.trim()) { toast.error('El nombre del proveedor es requerido'); return; }
    try {
      const res = await suppliersAPI.create(newSupplierForm);
      toast.success('Proveedor creado');
      setSuppliers([...suppliers, res.data]);
      setMovementForm({...movementForm, supplier_id: res.data.id, supplier: res.data.name});
      setNewSupplierDialogOpen(false);
      setNewSupplierForm({ name: '', rnc: '', phone: '', email: '' });
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al crear proveedor'); }
  };

  const handleSuperUserVerified = () => {
    if (!pendingAction) return;
    switch (pendingAction.type) {
      case 'edit_material': executeEditMaterial(pendingAction.data); break;
      case 'edit_movement': executeEditMovement(pendingAction.data); break;
      case 'delete_material': setDeleteType('material'); setItemToDelete(pendingAction.data); setDeleteDialogOpen(true); break;
      case 'delete_movement': setDeleteType('movement'); setItemToDelete(pendingAction.data); setDeleteDialogOpen(true); break;
      case 'initial_stock_cost': setInitialStockCostDialogOpen(true); break;
      default: break;
    }
    setPendingAction(null);
  };

  const handleExportExcel = async () => {
    try {
      toast.info('Generando reporte Excel...');
      
      const token = localStorage.getItem('avipork_token');
      if (!token) {
        toast.error('Sesión expirada');
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/inventory/excel`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Error en la descarga');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventario_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Reporte descargado');
    } catch (error) { 
      console.error('Error downloading report:', error);
      toast.error('Error al descargar reporte'); 
    }
  };

  // Computed values
  const filteredMaterials = materials.filter(m => {
    const matchesSearch = m.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = selectedMaterialFilter === 'all' || m.id === selectedMaterialFilter;
    return matchesSearch && matchesFilter;
  });

  const totalsByMaterial = materials.reduce((acc, m) => {
    const currency = m.currency || 'DOP';
    if (!acc[currency]) acc[currency] = 0;
    acc[currency] += m.total_value;
    return acc;
  }, {});
  
  const lowStockCount = materials.filter(m => m.current_stock <= m.min_stock).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-[#931A25] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Inventario</h1>
          <p className="text-slate-500 mt-1">Gestión de materias primas</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportExcel} data-testid="export-excel-btn">
            <Download className="h-4 w-4 mr-2" />Excel
          </Button>
          <MovementsSheet
            movements={movements}
            materials={materials}
            exchangeRate={exchangeRate}
            isSuperUser={isSuperUser}
            onEdit={handleEditMovement}
            onDelete={handleDeleteMovement}
          />
          <Button variant="outline" onClick={handleOpenInitialStockCost} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="initial-stock-cost-btn">
            <Database className="h-4 w-4 mr-2" />Stock / Costo Inicial
          </Button>
          <MaterialDialog
            open={materialDialogOpen}
            onOpenChange={setMaterialDialogOpen}
            formData={newMaterial}
            setFormData={setNewMaterial}
            onSubmit={handleCreateMaterial}
            onReset={resetMaterialForm}
            editingMaterial={editingMaterial}
            materials={materials}
          />
        </div>
      </div>

      {/* Stats */}
      <InventoryStats materials={materials} totalsByMaterial={totalsByMaterial} lowStockCount={lowStockCount} />

      {/* Search and Movement Button */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Buscar material..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-material-input"
          />
        </div>
        <MovementDialog
          formData={movementForm}
          setFormData={setMovementForm}
          onSubmit={handleCreateMovement}
          materials={materials}
          suppliers={suppliers}
          conversionPreview={conversionPreview}
          onAddSupplier={() => setNewSupplierDialogOpen(true)}
        />
      </div>

      {/* Materials Table */}
      <MaterialsTable
        materials={materials}
        filteredMaterials={filteredMaterials}
        selectedMaterialFilter={selectedMaterialFilter}
        setSelectedMaterialFilter={setSelectedMaterialFilter}
        exchangeRate={exchangeRate}
        isSuperUser={isSuperUser}
        onEdit={handleEditMaterial}
        onDelete={handleDeleteMaterial}
        onToggleDeduct={handleToggleDeduct}
      />

      {/* Movements — Panel deslizante controlado desde el header (botón "Movimientos Recientes") */}

      {/* Edit Movement Dialog */}
      <EditMovementDialog
        open={editMovementDialogOpen}
        onOpenChange={setEditMovementDialogOpen}
        formData={editMovementForm}
        setFormData={setEditMovementForm}
        onSubmit={handleUpdateMovement}
        editingMovement={editingMovement}
        suppliers={suppliers}
      />

      {/* Supplier Dialog */}
      <SupplierDialog
        open={newSupplierDialogOpen}
        onOpenChange={setNewSupplierDialogOpen}
        formData={newSupplierForm}
        setFormData={setNewSupplierForm}
        onSubmit={handleCreateSupplier}
      />

      {/* Initial Stock & Cost Dialog (Combined) */}
      <InitialStockCostDialog
        open={initialStockCostDialogOpen}
        onOpenChange={setInitialStockCostDialogOpen}
        formData={initialStockCostForm}
        setFormData={setInitialStockCostForm}
        onSave={handleSaveInitialStockCost}
        materials={materials}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirmar eliminación?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteType === 'material' 
                ? 'Esta acción eliminará el material y todos sus movimientos asociados. Esta acción no se puede deshacer.'
                : 'Esta acción eliminará el movimiento y ajustará el stock automáticamente. Esta acción no se puede deshacer.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Super User Dialog */}
      <SuperUserDialog
        open={superUserDialogOpen}
        onOpenChange={setSuperUserDialogOpen}
        onSuccess={handleSuperUserVerified}
        title="Acción Restringida"
        description="Esta acción requiere verificación de super usuario."
      />
    </div>
  );
}
